import { useRef, useState } from 'react';
import type { MediaItem } from '@icetus/contracts';
import { MAX_UPLOAD_BYTES } from '@icetus/contracts';
import { Badge, Button, type ButtonVariant } from '@icetus/ui';
import { ApiError, confirmMedia, presignMedia, putToStorage } from '../api';
import { Modal } from './overlay';

/**
 * 图片上传按钮（直传 COS）。
 *
 * 为什么在浏览器里做三件"本该服务端做"的事：
 *   - **算 sha256**：用于同站点内去重。服务端拿不到文件，算不了。
 *   - **解宽高**：createImageBitmap 是浏览器原生的解码能力。服务端要拿到宽高
 *     只有两条路 —— 引入 sharp（原生二进制，Windows 与 Linux 的产物不同，
 *     这个项目已经吃过一次绑定平台的苦）或者自己解析 JPEG/PNG 头部
 *     （几百行、覆盖不全）。浏览器解码是零成本且永远准的。
 *   - **PUT 到 COS**：这就是直传的意义，文件字节不进我们的服务器。
 *
 * 服务端仍然保留最终裁决权：confirm 时用 HeadObject 核对真实大小与类型，
 * 申报值只是"申请"，对不上就不让这条记录变成可用。
 */

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/gif';

/** 浏览器算内容哈希。http 环境下 crypto.subtle 不存在，此时退化为不去重。 */
async function sha256Hex(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const buffer = await file.arrayBuffer();
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // 文件极大或解码失败：不影响上传，只是这次不参与去重
    return null;
  }
}

async function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return size;
  } catch {
    // GIF / 异常文件可能解码失败。宽高只是展示优化，拿不到就留空，
    // 不阻断上传 —— 否则客户会看到"图片传不上去"却没有任何原因。
    return null;
  }
}

export interface UploadProgress {
  filename: string;
  index: number;
  total: number;
}

export function ImageUploader({
  tenantId,
  siteId,
  onUploaded,
  label = '上传图片',
  multiple = true,
  disabled = false,
  variant = 'ghost',
  size = 'md',
}: {
  tenantId: string;
  siteId: string;
  onUploaded: (items: MediaItem[]) => void;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  /**
   * 按钮外观。默认 ghost —— 上传在页面编辑器、商品图集、站点设置里都是
   * 「旁边还有一个更大的动作」的次要项（选图、保存），上品牌色会跟主按钮抢；
   * 只有媒体库那种"整页就是为传图而生"的位置才传 primary。
   */
  variant?: ButtonVariant;
  /**
   * 按钮尺寸。默认 md。站点设置里「上传 / 媒体库 / 移除」三颗要挤在一列
   * 320px 宽的预览框下面，md 放不下会折行，那里传 sm。
   */
  size?: 'sm' | 'md';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reused, setReused] = useState(0);

  const uploadOne = async (file: File): Promise<MediaItem> => {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error(
        `「${file.name}」超过 ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB，请先压缩`,
      );
    }
    if (file.size === 0) throw new Error(`「${file.name}」是空文件`);

    const [dimensions, checksum] = await Promise.all([readDimensions(file), sha256Hex(file)]);

    const presigned = await presignMedia(tenantId, {
      siteId,
      filename: file.name,
      mime: file.type,
      bytes: file.size,
      width: dimensions?.width ?? null,
      height: dimensions?.height ?? null,
      checksum,
    });

    // 同内容的图已经有了：直接用，不再传一遍
    if (presigned.reused) {
      setReused((n) => n + 1);
      return presigned.media;
    }

    const { ticket } = presigned;
    // 与资料库同一套直传：`fetch` 报不出上传进度，10MB 的图在慢速上行上
    // 能传一分多钟，只显示「上传中 1/2」等于什么都没说
    let lastEmit = 0;
    await putToStorage(ticket, file, (sent, total) => {
      const now = performance.now();
      if (now - lastEmit < 120) return;
      lastEmit = now;
      setPercent(total > 0 ? Math.round((sent / total) * 100) : 0);
    });

    // 服务端核对真实大小/类型后才置为可用
    return confirmMedia(tenantId, ticket.mediaId, {
      width: dimensions?.width ?? null,
      height: dimensions?.height ?? null,
    });
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    setReused(0);
    const chosen = Array.from(files).slice(0, multiple ? 12 : 1);
    const done: MediaItem[] = [];
    const failures: string[] = [];

    for (const [index, file] of chosen.entries()) {
      setProgress({ filename: file.name, index: index + 1, total: chosen.length });
      // 每份从头开始：不清掉的话，第二张图会从上一张的 100% 接着显示，
      // 看起来像"传完了却不动"
      setPercent(0);
      try {
        done.push(await uploadOne(file));
      } catch (err) {
        failures.push(err instanceof ApiError ? `${file.name}：${err.message}` : (err as Error).message);
      }
    }

    setProgress(null);
    if (done.length > 0) onUploaded(done);
    // 一批里部分失败时，把成功的先交出去，再报失败的 —— 让客户不用重传全部
    if (failures.length > 0) setError(failures.join('；'));
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="uploader">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <Button
        variant={variant}
        size={size}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      {!busy && reused > 0 ? (
        <Badge tone="success">其中 {reused} 张已存在，已直接复用</Badge>
      ) : null}
      {error ? <span className="uploader__error">{error}</span> : null}

      {/*
        进度放弹层而不是按钮上。
        工具栏里那行「上传中 2/5 · 45%」有两个问题：字号只有 13px，慢速上行
        传十张时它一直在变，眼睛会一直往那儿瞟；而且按钮宽度随数字跳动，
        旁边的「共 N 张」跟着左右挪。弹层里能放一条真的进度条，
        顺带挡住"再点一次上传"这种重复操作（上传中按钮本来也禁用了）。
        不可关闭：关掉它并不能中断上传，只会让人以为传了一半就停了。
      */}
      <Modal
        open={busy}
        onClose={() => {}}
        closable={false}
        title="正在上传"
        size="md"
      >
        <div className="upload-progress">
          <p className="upload-progress__name">{progress?.filename ?? '准备中…'}</p>
          {/* 复用「资料上传」那套轨道与填充色：两处的进度说的是同一件事，
              长得不一样只会让人以为是两个功能 */}
          <div
            className="uploadlist__track"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="uploadlist__fill" style={{ inlineSize: `${percent}%` }} />
          </div>
          <p className="muted upload-progress__meta">
            {[
              // 只传一张时「第 1 / 共 1 张」是废话；多张才值得说
              progress && progress.total > 1 ? `第 ${progress.index} / 共 ${progress.total} 张` : '',
              // 百分数是字节点节流出来的，第一帧还没到，写"正在读取"比让条空着好
              percent > 0 ? `${percent}%` : '正在读取…',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </Modal>
    </div>
  );
}
